import { useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import paymentService from '@/services/paymentService';
import { Pago } from '@/types';
import { useDialog } from '@/hooks/useDialog';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  payment: Pago | null;
  onPaymentUpdated: () => void;
};

export default function RecordPartialPaymentModal({ isOpen, onClose, payment, onPaymentUpdated }: Props) {
  const { showAlert } = useDialog();
  const [form, setForm] = useState({
    amount: '',
    metodo_pago: 'EFECTIVO',
  });
  const [submitting, setSubmitting] = useState(false);

  if (!payment) return null;

  const remainingAmount = payment.monto - (payment.monto_pagado || 0);

  const handleSubmit = async () => {
    const amount = parseFloat(form.amount);

    if (!amount || amount <= 0) {
      showAlert({
        type: 'warning',
        title: 'Monto inválido',
        message: 'Por favor ingrese un monto válido',
      });
      return;
    }

    if (amount > remainingAmount) {
      showAlert({
        type: 'warning',
        title: 'Monto excedido',
        message: `El monto no puede ser mayor al saldo pendiente (${payment.moneda} ${remainingAmount.toFixed(2)})`,
      });
      return;
    }

    if (!form.metodo_pago) {
      showAlert({
        type: 'warning',
        title: 'Campo requerido',
        message: 'Por favor seleccione un método de pago',
      });
      return;
    }

    setSubmitting(true);
    try {
      await paymentService.recordPartialPayment(Number(payment.id), {
        amount,
        metodo_pago: form.metodo_pago,
      });

      showAlert({
        type: 'success',
        title: 'Éxito',
        message: 'Pago parcial registrado exitosamente',
      });
      setForm({ amount: '', metodo_pago: 'EFECTIVO' });
      onPaymentUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error recording partial payment:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || 'Error al registrar el pago parcial',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${payment.moneda} ${amount.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Registrar Pago Parcial" size="md">
      <div className="space-y-4">
        {/* Payment Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <h4 className="font-medium text-blue-900 mb-2">Información del Pago</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-blue-700">Monto Total:</p>
              <p className="font-semibold text-blue-900">{formatCurrency(payment.monto)}</p>
            </div>
            <div>
              <p className="text-blue-700">Monto Pagado:</p>
              <p className="font-semibold text-blue-900">{formatCurrency(payment.monto_pagado || 0)}</p>
            </div>
            <div className="col-span-2">
              <p className="text-blue-700">Saldo Pendiente:</p>
              <p className="font-semibold text-blue-900 text-lg">{formatCurrency(remainingAmount)}</p>
            </div>
          </div>
        </div>

        {/* Partial Payment Form */}
        <div className="space-y-3">
          <Input
            label="Monto a Pagar"
            type="number"
            step="0.01"
            min="0.01"
            max={remainingAmount}
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder={`Máximo: ${formatCurrency(remainingAmount)}`}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Método de Pago <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.metodo_pago}
              onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })}
            >
              <option value="EFECTIVO">Efectivo</option>
              <option value="TRANSFERENCIA">Transferencia Bancaria</option>
              <option value="CHEQUE">Cheque</option>
              <option value="TARJETA">Tarjeta</option>
              <option value="DEBITO_AUTOMATICO">Débito Automático</option>
            </select>
          </div>
        </div>

        {/* Info Message */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <p className="text-sm text-yellow-800">
            <strong>Nota:</strong> Al registrar este pago parcial, se generará automáticamente un recibo.
            {parseFloat(form.amount) > 0 && remainingAmount - parseFloat(form.amount) > 0 && (
              <span className="block mt-1">
                Después de este pago, quedará un saldo pendiente de {formatCurrency(remainingAmount - parseFloat(form.amount))}.
              </span>
            )}
            {parseFloat(form.amount) === remainingAmount && (
              <span className="block mt-1 text-green-700 font-semibold">
                Este pago completará el saldo total del pago.
              </span>
            )}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t">
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Registrando...' : 'Registrar Pago Parcial'}
          </Button>
          <Button onClick={onClose} variant="secondary">
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
